import { FunctionComponent, useMemo, useState } from "react";
import { ThemeStatisticsItem, ThemeStatisticsReport } from "../../models/ThemeStatistics";
import { getUpCareThemeDisplay, UPCARE_THEMES, UpCareThemeCode } from "../../utils/UpCareThemes";
import styles from "./ThemeAnalyticsWidget.module.css";

type ChartMode = "bar" | "pie";

interface ThemeAnalyticsWidgetProps {
    report: ThemeStatisticsReport;
    selectedTheme?: UpCareThemeCode;
    onThemeSelect?: (theme: UpCareThemeCode) => void;
    onShowActions?: (theme: UpCareThemeCode) => void;
}

const THEME_COLORS = [
    "#2f6b86", "#5f8fa6", "#83aebc", "#e0a85b", "#d98772",
    "#9b7eb8", "#70a27f", "#a3a85e", "#718696"
];

const ThemeAnalyticsWidget: FunctionComponent<ThemeAnalyticsWidgetProps> = ({
    report,
    selectedTheme,
    onThemeSelect,
    onShowActions
}) => {
    const [chartMode, setChartMode] = useState<ChartMode>("bar");
    const rankedThemes = useMemo(
        () => [...report.themes].sort((left, right) => right.total - left.total || left.label.localeCompare(right.label, "fr")),
        [report.themes]
    );
    const selected = report.themes.find((theme) => theme.theme === selectedTheme) ?? rankedThemes.find((theme) => theme.total > 0);
    const max = Math.max(1, ...rankedThemes.map((theme) => theme.total));
    const pieBackground = buildPieBackground(rankedThemes, report.total);

    return (
        <section className={styles.widget}>
            <div className={styles.kpis}>
                <Kpi value={String(report.total)} label="Actions thématisées" />
                <Kpi value={report.dominantTheme ? getUpCareThemeDisplay(report.dominantTheme) ?? "—" : "—"} label="Thématique principale" />
                <Kpi value={`${report.activeThemeCount} / ${UPCARE_THEMES.length}`} label="Thématiques actives" />
            </div>

            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <div>
                        <h2>Actions par thématique</h2>
                        <p>Cumul des besoins, solutions et ateliers selon les filtres actifs.</p>
                    </div>
                    <div className={styles.chartMode} role="group" aria-label="Type de graphique">
                        <button type="button" className={chartMode === "bar" ? styles.activeMode : undefined} onClick={() => setChartMode("bar")}>Histogramme</button>
                        <button type="button" className={chartMode === "pie" ? styles.activeMode : undefined} onClick={() => setChartMode("pie")}>Répartition</button>
                    </div>
                </div>

                {report.total === 0 ? (
                    <p className={styles.empty}>Aucune action thématisée ne correspond aux filtres.</p>
                ) : chartMode === "bar" ? (
                    <div className={styles.bars}>
                        {rankedThemes.map((theme) => (
                            <button
                                key={theme.theme}
                                type="button"
                                className={`${styles.barRow} ${selected?.theme === theme.theme ? styles.selectedBar : ""}`}
                                onClick={() => onThemeSelect?.(theme.theme)}
                            >
                                <span className={styles.barLabel}>{theme.label}</span>
                                <span className={styles.barTrack} aria-hidden="true">
                                    <span style={{ width: `${(theme.total / max) * 100}%` }} />
                                </span>
                                <strong>{theme.total}</strong>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className={styles.pieLayout}>
                        <div className={styles.pie} style={{ background: pieBackground }} aria-label={`${report.total} actions réparties par thématique`}>
                            <div><strong>{report.total}</strong><span>actions</span></div>
                        </div>
                        <div className={styles.legend}>
                            {rankedThemes.filter((theme) => theme.total > 0).map((theme, index) => (
                                <button key={theme.theme} type="button" onClick={() => onThemeSelect?.(theme.theme)}>
                                    <i style={{ background: THEME_COLORS[index % THEME_COLORS.length] }} />
                                    <span>{theme.label}</span>
                                    <strong>{Math.round((theme.total / report.total) * 100)} %</strong>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {selected && selected.total > 0 && (
                <section className={styles.breakdown}>
                    <div className={styles.breakdownHeader}>
                        <div>
                            <span>Thématique sélectionnée</span>
                            <h3>{selected.label}</h3>
                        </div>
                        {onShowActions && (
                            <button type="button" onClick={() => onShowActions(selected.theme)}>Voir les {selected.total} actions</button>
                        )}
                    </div>
                    <div className={styles.breakdownGrid}>
                        <BreakdownValue label="Besoins" value={selected.requests} />
                        <BreakdownValue label="Solutions" value={selected.proposals} />
                        <BreakdownValue label="Programmés" value={selected.scheduled} />
                        <BreakdownValue label="Réalisés" value={selected.completed} />
                    </div>
                </section>
            )}

            {report.source === "front" && (
                <small className={styles.source}>Évaluation POC calculée côté front. Le widget consomme un rapport indépendant de la future implémentation FHIR Measure.</small>
            )}
        </section>
    );
};

const Kpi: FunctionComponent<{ value: string; label: string }> = ({ value, label }) => (
    <div className={styles.kpi}><strong>{value}</strong><span>{label}</span></div>
);

const BreakdownValue: FunctionComponent<{ label: string; value: number }> = ({ label, value }) => (
    <div className={styles.breakdownValue}><strong>{value}</strong><span>{label}</span></div>
);

const buildPieBackground = (themes: ThemeStatisticsItem[], total: number): string => {
    if (total <= 0) return "#edf1f3";
    let cursor = 0;
    const segments = themes
        .filter((theme) => theme.total > 0)
        .map((theme, index) => {
            const start = cursor;
            cursor += (theme.total / total) * 100;
            return `${THEME_COLORS[index % THEME_COLORS.length]} ${start}% ${cursor}%`;
        });
    return `conic-gradient(${segments.join(", ")})`;
};

export default ThemeAnalyticsWidget;
